<template>
  <section>
    <b-card v-if="!notificationList.length">
      <div class="d-flex flex-column no-notifications">
        <GjIcon
          class="mb-2"
          size="60"
          name="Notifications"
        />
        <span>{{ $tk('ClientPortal.Notification.ThereAreNoNotifications') }}</span>
      </div>
    </b-card>
    <b-container
      v-else
      class="container-736px pb-2"
    >
      <div class="mb-2">
        <b-row>
          <b-col sm="12">
            <div class="d-flex justify-content-between align-items-center h-100">
              <h6 class="mb-0 font-weight-bolder">
                {{ $tk('ClientPortal.Notification.Notifications') }}
              </h6>
              <b-link
                class="text-dark"
                @click="readAllNotifications"
              >
                {{ $tk('ClientPortal.Notification.MarkAllAsRead') }}
              </b-link>
            </div>
          </b-col>
        </b-row>
      </div>
      <app-timeline
        class="ml-n2-50"
      >
        <app-timeline-item
          v-for="notificationList in groupedNotificationsByDate"
          :key="notificationList[0].dateTime"
          variant="secondary"
          class="mb-n2-25"
        >
          <div
            class="timeline-item-date"
          >
            <div class="mt-n25">
              {{ notificationList[0].dateTime | formatDate }}
            </div>
            <div
              class="timeline-item-point"
            />
          </div>

          <b-card
            v-for="notification in notificationList"
            :key="notification.id"
            class="mb-1"
          >
            <b-media>
              <template #aside>
                <b-avatar
                  size="32"
                  :src="notification.account.avatar"
                  :text="notification.account.displayName"
                />
              </template>
              <p class="media-heading d-flex justify-content-between">
                <span class="font-weight-bolder">
                  {{ notification.card.title }}
                </span>
                <small class="text-muted">
                  {{ notification.relativeDateTime }}
                  <i
                    v-if="notification.status == newNotificationStatus"
                    class="dot ml-50"
                  />
                </small>
              </p>
              <hr>
              <p class="mb-50">
                <span class="text-muted">{{ $tk('ClientPortal.Notification.Description') }}:</span>
                {{ notification.card.description }}
              </p>
              <p
                v-for="(metaData,index) in notification.card.metadata"
                :key="index"
                class="mb-50"
              >
                <span class="text-muted">{{ metaData.key }}: </span>
                {{ metaData.value }}
              </p>
              <b-link @click="redirectToNotification(notification)">
                {{ notification.buttonText }}
              </b-link>
              <b-link
                class="ml-1"
                @click="deleteNotification(notification)"
              >
                {{ $tk('ClientPortal.Notification.Delete') }}
              </b-link>
            </b-media>
          </b-card>
        </app-timeline-item>
        <InfiniteLoading
          spinner="spiral"
          @infinite="getNextNotifications"
        >
          <div
            slot="no-results"
            class="mt-2 pt-2"
          />
          <div
            slot="no-more"
            class="mt-2 pt-2"
          />
          <div
            slot="spinner"
            class="mt-2 pt-2"
          >
            <app-timeline-item
              v-for="(i, index) in 2"
              :key="index"
              variant="secondary"
            >
              <b-card class="mb-0">
                <b-row>
                  <b-col sm="1">
                    <b-skeleton type="avatar" />
                  </b-col>
                  <b-col class="ml-1">
                    <b-row>
                      <b-col sm="10">
                        <b-skeleton
                          animation="wave"
                          width="60%"
                        />
                      </b-col>
                      <b-col sm="2">
                        <b-skeleton
                          animation="wave"
                          width="100%"
                        />
                      </b-col>
                    </b-row>
                    <hr class="mt-0">
                    <b-skeleton
                      animation="wave"
                      width="65%"
                    />
                    <b-skeleton
                      animation="wave"
                      width="40%"
                    />
                    <b-row>
                      <b-col sm="2">
                        <b-skeleton
                          animation="wave"
                          width="100%"
                        />
                      </b-col>
                      <b-col sm="2">
                        <b-skeleton
                          animation="wave"
                          width="100%"
                        />
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-card>
            </app-timeline-item>
          </div>
        </InfiniteLoading>
      </app-timeline>
    </b-container>
  </section>
</template>

<script>
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import notification from '@/mixins/toastification'
import moment from 'moment'
import InfiniteLoading from 'vue-infinite-loading'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import {
  mapGetters,
  mapActions,
} from 'vuex'

const NOTIFICATION_STATUS = {
  NEW: 0,
  READ: 1,
  CLICKED: 2,
  DELETED: 3,
}

export default {
  components: {
    AppTimeline,
    AppTimelineItem,
    InfiniteLoading,
    GjIcon,
  },
  filters: {
    formatDate(value) {
      return moment(value, 'DD/MM/YYYY hh:mm').format('DD MMM, YYYY')
    },
  },
  mixins: [notification],
  data() {
    return {
      newNotificationStatus: 0,
      infinityLoading: false,
    }
  },
  computed: {
    ...mapGetters(['notificationList']),
    groupedNotificationsByDate() {
      return this.notificationList.reduce((grouped, notif) => {
        (grouped[moment(notif.dateTime, 'DD/MM/YYYY')] = grouped[moment(notif.dateTime, 'DD/MM/YYYY')] || []).push(notif)
        return grouped
      }, {})
    },
  },
  methods: {
    ...mapActions(['getNotifications', 'update', 'delete', 'readAll']),
    async readAllNotifications() {
      try {
        await this.readAll()
      } catch (error) {
        this.error(error.message)
      }
    },
    async deleteNotification(notificationInfo) {
      try {
        const response = await this.delete(notificationInfo.id)
        if (this.notificationList.length === 1) {
          await this.getNotifications(0)
        }
        if (!response.updated) {
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async redirectToNotification(notificationInfo) {
      if (notificationInfo.status !== NOTIFICATION_STATUS.CLICKED) {
        const response = await this.update({ notificationId: notificationInfo.id, status: NOTIFICATION_STATUS.CLICKED })
        if (response.updated) {
          this.$router.push(notificationInfo.redirectUrlSPA)
        } else {
          this.error(response.message)
        }
      } else {
        this.$router.push(notificationInfo.redirectUrlSPA)
      }
    },
    async getNextNotifications($state) {
      this.infinityLoading = true
      try {
        const { notifications } = await this.getNotifications()
        if (notifications.length === 0) {
          $state.complete()
        } else {
          $state.loaded()
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.infinityLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/account/notifications.scss';
</style>
